// computeBraces.ts — Compute temporary brace geometry for tilt-up panels
// Rules from docs/source/tiltup design details/lifting-rigging-and-bracing.md §8
// bpvar-first: uses defined brace inserts if present, auto-computes otherwise.

import type { PanelData, BracePlanSettings } from '../types/panelvar';
import type { WallSegment } from '../components/SiteViewport3D';

export interface BraceGeometry {
  id: string;
  panelId: string;
  panelMark: string;
  insertX: number; insertY: number; insertZ: number;
  anchorX: number; anchorY: number; anchorZ: number;
  length: number;       // inches
  angleDeg: number;     // actual angle from horizontal
  isClash: boolean;
  clashType?: 'brace-brace' | 'brace-panel';
  clashWith?: string[]; // BraceGeometry ids
}

const PIPE_RADIUS = 4; // inches — nominal 3" pipe OD ≈ 3.5", use 4 for clearance

// ── Layout helper (mirrors segmentLayout in SiteViewer3D) ────────────────────
function segmentLayout(panels: PanelData[], jointWidth: number, leftEnd: number, rightEnd: number) {
  const offsets: number[] = [];
  let x = leftEnd;
  for (let i = 0; i < panels.length; i++) {
    offsets.push(x);
    const gap = i < panels.length - 1 ? jointWidth : rightEnd;
    x += panels[i].mpvar.width + gap;
  }
  return offsets;
}

// ── 3D segment-to-segment minimum distance ────────────────────────────────────
// Returns squared minimum distance between line segments AB and CD
function segSegDistSq(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  dx: number, dy: number, dz: number,
): number {
  const ux = bx - ax, uy = by - ay, uz = bz - az;
  const vx = dx - cx, vy = dy - cy, vz = dz - cz;
  const wx = ax - cx, wy = ay - cy, wz = az - cz;
  const a = ux*ux + uy*uy + uz*uz;
  const b = ux*vx + uy*vy + uz*vz;
  const c = vx*vx + vy*vy + vz*vz;
  const d = ux*wx + uy*wy + uz*wz;
  const e = vx*wx + vy*wy + vz*wz;
  const D = a*c - b*b;
  let sc: number, sN: number, sD = D;
  let tc: number, tN: number, tD = D;
  if (D < 1e-7) {
    sN = 0; sD = 1; tN = e; tD = c;
  } else {
    sN = b*e - c*d;
    tN = a*e - b*d;
    if (sN < 0) { sN = 0; tN = e; tD = c; }
    else if (sN > sD) { sN = sD; tN = e + b; tD = c; }
  }
  if (tN < 0) { tN = 0; if (-d < 0) sN = 0; else if (-d > a) sN = sD; else { sN = -d; sD = a; } }
  else if (tN > tD) { tN = tD; if (-d + b < 0) sN = 0; else if (-d + b > a) sN = sD; else { sN = -d + b; sD = a; } }
  sc = Math.abs(sN) < 1e-7 ? 0 : sN / sD;
  tc = Math.abs(tN) < 1e-7 ? 0 : tN / tD;
  const nx = wx + sc*ux - tc*vx;
  const ny = wy + sc*uy - tc*vy;
  const nz = wz + sc*uz - tc*vz;
  return nx*nx + ny*ny + nz*nz;
}

// ── OBB definition for a panel ─────────────────────────────────────��──────────
interface PanelOBB {
  panelId: string;
  cx: number; cy: number; cz: number;
  hw: number; hh: number; ht: number; // half-width, half-height, half-thickness
  cosA: number; sinA: number;          // from segment rotationY
}

function buildPanelOBBs(segments: WallSegment[], jointWidth: number): PanelOBB[] {
  const obbs: PanelOBB[] = [];
  for (const seg of segments) {
    const offsets = segmentLayout(seg.panels, jointWidth, seg.leftEndJoint, seg.rightEndJoint);
    const cosA = Math.cos(seg.rotationY);
    const sinA = Math.sin(seg.rotationY);
    const baseY = seg.baseElevation ?? 0;
    for (let pi = 0; pi < seg.panels.length; pi++) {
      const p = seg.panels[pi];
      const mp = p.mpvar;
      const offset = offsets[pi] + mp.width / 2; // center along wall
      // world center
      const cx = seg.x + offset * cosA;
      const cy = baseY + (p.mpvar.botElev ?? 0) * 12 + mp.height / 2;
      const cz = seg.z - offset * sinA;
      obbs.push({ panelId: p.id, cx, cy, cz, hw: mp.width / 2, hh: mp.height / 2, ht: (mp.thickness ?? 7.25) / 2, cosA, sinA });
    }
  }
  return obbs;
}

// ── Line segment vs OBB intersection (slab method in OBB local space) ────────
function lineIntersectsOBB(
  p1x: number, p1y: number, p1z: number,
  p2x: number, p2y: number, p2z: number,
  obb: PanelOBB,
): boolean {
  // Transform line to OBB local space (Y-rotation only)
  const r1x = p1x - obb.cx, r1z = p1z - obb.cz;
  const r2x = p2x - obb.cx, r2z = p2z - obb.cz;
  // Rotate by -rotY: local = [cos*rx + sin*rz, ry, -sin*rx + cos*rz]
  const l1x =  obb.cosA * r1x + obb.sinA * r1z;
  const l1y = p1y - obb.cy;
  const l1z = -obb.sinA * r1x + obb.cosA * r1z;
  const l2x =  obb.cosA * r2x + obb.sinA * r2z;
  const l2y = p2y - obb.cy;
  const l2z = -obb.sinA * r2x + obb.cosA * r2z;
  // Slab test along each axis
  const slabs = [
    [l1x, l2x, -obb.hw, obb.hw],
    [l1y, l2y, -obb.hh, obb.hh],
    [l1z, l2z, -obb.ht, obb.ht],
  ] as const;
  let tmin = 0, tmax = 1;
  for (const [a, b, lo, hi] of slabs) {
    const d = b - a;
    if (Math.abs(d) < 1e-9) { if (a < lo || a > hi) return false; continue; }
    const t1 = (lo - a) / d, t2 = (hi - a) / d;
    tmin = Math.max(tmin, Math.min(t1, t2));
    tmax = Math.min(tmax, Math.max(t1, t2));
    if (tmin > tmax) return false;
  }
  return true;
}

// ── Main export ────────────────────────────────────────────────────────────────
export function computeBraces(
  segments: WallSegment[],
  allPanels: PanelData[],
  checkedPanelIds: Set<string>,
  globalSettings: BracePlanSettings,
  panelOverrides: Record<string, Partial<BracePlanSettings>>,
  jointWidth = 0.75,
): BraceGeometry[] {
  void allPanels; // panels accessed via segments; kept for future direct-panel lookups
  const braces: BraceGeometry[] = [];
  const panelOBBs = buildPanelOBBs(segments, jointWidth);

  for (const seg of segments) {
    const offsets = segmentLayout(seg.panels, jointWidth, seg.leftEndJoint, seg.rightEndJoint);
    const cosA = Math.cos(seg.rotationY);
    const sinA = Math.sin(seg.rotationY);
    const baseY = seg.baseElevation ?? 0;

    for (let pi = 0; pi < seg.panels.length; pi++) {
      const p = seg.panels[pi];
      if (!checkedPanelIds.has(p.id)) continue;
      const mp = p.mpvar;
      const override = panelOverrides[p.id] ?? {};
      const settings: BracePlanSettings = { ...globalSettings, ...override };

      // Interior face = local +Z = world (sin(rotY), 0, cos(rotY))
      // PanelMesh3D applies rotation=[0,PI,0] so its -Z face is in local +Z of the segment group
      const intSign = settings.braceInterior !== false ? 1 : -1;
      const intDX = sinA * intSign;
      const intDZ = cosA * intSign;

      const ht = (mp.thickness ?? 7.25) / 2;
      const panelOffsetX = offsets[pi]; // left edge from seg origin

      // bpvar-first: use defined brace insert positions if any are populated
      const useBpvar = (p.bpvar ?? []).some(slot => slot && (slot as { x?: number }).x != null && (slot as { x?: number }).x! > 0);
      const insertPositions: Array<{ localX: number; localY: number }> = [];

      if (useBpvar) {
        for (const slot of (p.bpvar ?? [])) {
          const bpd = slot as { x?: number; y?: number } | null;
          if (bpd && bpd.x != null && bpd.x > 0) {
            insertPositions.push({ localX: panelOffsetX + bpd.x, localY: bpd.y ?? mp.height * (settings.insertHeightPct / 100) });
          }
        }
      }

      if (insertPositions.length === 0) {
        // Auto-compute: evenly spaced, capped to n max by spacing rule
        const n = Math.max(settings.minBraceCount, Math.ceil(mp.width / settings.maxSpacingIn));
        const braceY = mp.height * (settings.insertHeightPct / 100);
        for (let j = 0; j < n; j++) {
          const localX = panelOffsetX + (j + 1) * mp.width / (n + 1);
          insertPositions.push({ localX, localY: braceY });
        }
      }

      for (let bi = 0; bi < insertPositions.length; bi++) {
        const { localX, localY } = insertPositions[bi];
        // Insert world position (interior face, half-thickness inward from panel center)
        const insertX = seg.x + localX * cosA + ht * intDX;
        const insertZ = seg.z - localX * sinA + ht * intDZ;
        const insertY = baseY + (mp.botElev ?? 0) * 12 + localY;

        const heightAboveSlab = insertY - baseY;
        if (heightAboveSlab <= 0) continue; // degenerate

        const angleRad = settings.braceAngleDeg * Math.PI / 180;
        const horizDist = heightAboveSlab / Math.tan(angleRad);
        const anchorX = insertX + horizDist * intDX;
        const anchorZ = insertZ + horizDist * intDZ;
        const anchorY = baseY;
        const length = Math.sqrt(horizDist * horizDist + heightAboveSlab * heightAboveSlab);
        const actualAngle = Math.atan2(heightAboveSlab, horizDist) * 180 / Math.PI;

        braces.push({
          id: `${p.id}-b${bi}`,
          panelId: p.id,
          panelMark: mp.panelMark || p.id,
          insertX, insertY, insertZ,
          anchorX, anchorY, anchorZ,
          length, angleDeg: actualAngle,
          isClash: false,
        });
      }
    }
  }

  // ── Clash pass 1: brace-to-brace ────────────────────────────────────────────
  const clashPairs = new Map<string, string[]>();
  const addClash = (id: string, otherId: string) => {
    if (!clashPairs.has(id)) clashPairs.set(id, []);
    clashPairs.get(id)!.push(otherId);
  };
  const thresh = (PIPE_RADIUS * 2) ** 2;
  for (let i = 0; i < braces.length; i++) {
    for (let j = i + 1; j < braces.length; j++) {
      const a = braces[i], b = braces[j];
      if (a.panelId === b.panelId) continue; // same panel braces don't clash with each other
      const d2 = segSegDistSq(
        a.insertX, a.insertY, a.insertZ, a.anchorX, a.anchorY, a.anchorZ,
        b.insertX, b.insertY, b.insertZ, b.anchorX, b.anchorY, b.anchorZ,
      );
      if (d2 < thresh) {
        addClash(a.id, b.id);
        addClash(b.id, a.id);
      }
    }
  }

  // ── Clash pass 2: brace-to-panel OBB ────────────────────────────────────────
  for (const brace of braces) {
    for (const obb of panelOBBs) {
      if (obb.panelId === brace.panelId) continue; // skip own panel
      if (lineIntersectsOBB(brace.insertX, brace.insertY, brace.insertZ, brace.anchorX, brace.anchorY, brace.anchorZ, obb)) {
        if (!clashPairs.has(brace.id)) clashPairs.set(brace.id, []);
        clashPairs.get(brace.id)!.push(`panel:${obb.panelId}`);
      }
    }
  }

  // Apply clash flags
  for (const brace of braces) {
    const clashList = clashPairs.get(brace.id);
    if (clashList && clashList.length > 0) {
      brace.isClash = true;
      brace.clashWith = clashList;
      brace.clashType = clashList.some(c => c.startsWith('panel:')) ? 'brace-panel' : 'brace-brace';
    }
  }

  return braces;
}
